import {IParkingOption} from "../types/parkingOptions.type";
import {TFunction} from "i18next";
import {DateTime} from "luxon";
import { PriceCalculationMulti, PriceCalculationSingle } from "types/priceCalculation.type";

export class ParkingOptionsService {
    readonly t: TFunction<"translation", undefined>;

    constructor( t: TFunction<"translation", undefined>) {
        this.t = t;
    }

    getParkingTimeOptions = async (
        priceByMinutes: PriceCalculationMulti, 
        minPrice: number
    ): Promise<IParkingOption[]> => {
        const filteredPriceCalculations = priceByMinutes.priceCalculations.filter(c => 
            c.priceTotal >= minPrice && c.minutesAcceptedDuringParkingHours > 0
        );

        const firstOption = this.findFirstOption(filteredPriceCalculations);
        const firstHourOptions = this.getFirstHourOptions(filteredPriceCalculations, firstOption);

        const hourlyOptions = this.getHourlyOptions(filteredPriceCalculations);

        const operatingHoursEndOptions = this.getOperatingHoursEndOptions(filteredPriceCalculations, firstOption);

        const options = [...firstHourOptions, ...hourlyOptions, ...operatingHoursEndOptions];
        return options.sort((a, b) => a.value - b.value);

    }

    private getOptionLabel = (timeValue: number) => {
        if (timeValue <= 90) {
            return this.t(`timeOptions.${timeValue}minutes`);
        } else {
            return this.t(`timeOptions.${timeValue / 60}hours`);
        }
    };

    private toParkingOption = (
        priceCalculation: PriceCalculationSingle, 
        isDanger: boolean
    ): IParkingOption => {
        const end = DateTime.fromISO(priceCalculation.endOfParking);

        return {
            isDisabled: false,
            isDanger,
            optionHeaderText: `${this.t("buy.toDate")} ${end.toFormat("dd. MM. HH:mm")}`,
            value: priceCalculation.minutesAccepted,
            label: isDanger ? this.t("buy.untilClosingTime") : this.getOptionLabel(priceCalculation.minutesAcceptedDuringParkingHours),
            endOfParking: priceCalculation.endOfParking,
        }
    }

    private findFirstOption = (priceCalculations: PriceCalculationSingle[]): PriceCalculationSingle => {
        //const allowedIntervalMinutes: number[] = [10, 12, 15, 20, 30];
        const minInterval = 10;
        const validOptions = priceCalculations.filter(o => o.minutesAcceptedDuringParkingHours >= minInterval);
        const usedOptions = validOptions.length > 0 ? validOptions : priceCalculations;

        const minVal = usedOptions.reduce((acc, curr) =>
            acc.priceTotal === curr.priceTotal ? curr : acc
        );
        return minVal;
    }

    private getFirstHourOptions = (
        priceCalculations: PriceCalculationSingle[], 
        firstOption: PriceCalculationSingle
    ): IParkingOption[] => {
        const step = firstOption.minutesAcceptedDuringParkingHours;

        const options: IParkingOption[] = [];
        for (const c of priceCalculations) {
            if (c.minutesAcceptedDuringParkingHours <= 59 && c.minutesAcceptedDuringParkingHours % step === 0) {
                options.push(this.toParkingOption(c, false));
            }
            if (c.minutesAcceptedDuringParkingHours >= 60) {
                break;
            }
        }

        return options;
    }

    private getOperatingHoursEndOptions = (
        priceCalculations: PriceCalculationSingle[], 
        minOption: PriceCalculationSingle
    ): IParkingOption[] => {
        const options: IParkingOption[] = [];
        const included: number[] = [];

        priceCalculations.reduce((acc, curr) => {
            if (acc.priceTotal === curr.priceTotal && !included.includes(acc.priceTotal) && acc.priceTotal > minOption.priceTotal) {
                included.push(acc.priceTotal);
                options.push(this.toParkingOption(acc, true));
            }
            return curr;
        });
        return options;
    }

    private getHourlyOptions = (priceCalculations: PriceCalculationSingle[]): IParkingOption[] => {
        const options: IParkingOption[] = [];

        for (const c of priceCalculations) {
            if (c.minutesAcceptedDuringParkingHours % 60 === 0 && c.minutesAcceptedDuringParkingHours > 0) {
                options.push(this.toParkingOption(c, false));
            }
        }

        return options;
    }
}
